import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SecurityIcon from '@mui/icons-material/Security';
import MenuItem from '@mui/material/MenuItem';
import type { BugSource, Permission } from '../../types';
import type { AdminRole } from '../../types/admin';
import { rolesApi, permissionsApi } from '../../api/adminApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import { ConfirmDialog } from './ConfirmDialog';
import { RolePermissionDialog } from './RolePermissionDialog';

interface RoleFormState {
  open: boolean;
  mode: 'create' | 'edit';
  role: AdminRole | null;
  name: string;
  description: string;
  bugSource: BugSource;
  saving: boolean;
}

const EMPTY_FORM: RoleFormState = {
  open: false,
  mode: 'create',
  role: null,
  name: '',
  description: '',
  bugSource: 'TEAM',
  saving: false,
};

export function RoleManagement(): JSX.Element {
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<RoleFormState>(EMPTY_FORM);
  const [permissionRole, setPermissionRole] = useState<AdminRole | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminRole | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rolePage, permissionList] = await Promise.all([
        rolesApi.list({ limit: rowsPerPage, offset: page * rowsPerPage }),
        permissionsApi.list(),
      ]);
      setRoles(rolePage.items);
      setTotal(rolePage.total);
      setPermissions(permissionList);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải danh sách vai trò'), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, open: true, mode: 'create' });
  };

  const openEdit = (role: AdminRole) => {
    setForm({
      open: true,
      mode: 'edit',
      role,
      name: role.name,
      description: role.description ?? '',
      bugSource: role.bug_source,
      saving: false,
    });
  };

  const closeForm = () => setForm(EMPTY_FORM);

  const handleSubmitForm = async () => {
    if (!form.name.trim()) {
      notify('Vui lòng nhập tên vai trò', 'warning');
      return;
    }
    setForm((prev) => ({ ...prev, saving: true }));
    try {
      if (form.mode === 'create') {
        await rolesApi.create({
          name: form.name.trim(),
          description: form.description.trim() || null,
          bug_source: form.bugSource,
        });
        notify('Đã tạo vai trò mới', 'success');
      } else if (form.role) {
        await rolesApi.update(form.role.id, {
          name: form.name.trim(),
          description: form.description.trim() || null,
          bug_source: form.bugSource,
        });
        notify('Đã cập nhật vai trò', 'success');
      }
      closeForm();
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể lưu vai trò'), 'error');
      setForm((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await rolesApi.remove(deleteTarget.id);
      notify('Đã xóa vai trò', 'success');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xóa vai trò'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Quản lý vai trò
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Thêm vai trò
        </Button>
      </Stack>

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tên vai trò</TableCell>
                <TableCell>Mô tả</TableCell>
                <TableCell align="right">Hành động</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && roles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Chưa có vai trò nào.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                roles.map((role) => (
                  <TableRow key={role.id} hover>
                    <TableCell>
                      <Chip label={role.name} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>{role.description || '—'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Phân quyền">
                        <IconButton size="small" onClick={() => setPermissionRole(role)}>
                          <SecurityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sửa">
                        <IconButton size="small" onClick={() => openEdit(role)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Xóa">
                        <IconButton size="small" onClick={() => setDeleteTarget(role)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Số dòng/trang"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
        />
      </Paper>

      <Dialog open={form.open} onClose={form.saving ? undefined : closeForm} maxWidth="xs" fullWidth>
        <DialogTitle>{form.mode === 'create' ? 'Thêm vai trò' : 'Sửa vai trò'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Tên vai trò"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
              disabled={form.saving}
            />
            <TextField
              label="Mô tả"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
              disabled={form.saving}
            />
            <TextField
              select
              label="Nguồn bug mặc định"
              value={form.bugSource}
              onChange={(e) => setForm((prev) => ({ ...prev, bugSource: e.target.value as BugSource }))}
              fullWidth
              disabled={form.saving}
              helperText="User chỉ giữ vai trò này tạo bug sẽ bị gán nguồn tương ứng (Khách hàng = không thể chọn lại 'Team')."
            >
              <MenuItem value="TEAM">Team</MenuItem>
              <MenuItem value="CUSTOMER">Khách hàng</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm} disabled={form.saving}>
            Hủy
          </Button>
          <Button
            onClick={() => void handleSubmitForm()}
            variant="contained"
            disabled={form.saving}
            startIcon={form.saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {form.mode === 'create' ? 'Tạo mới' : 'Lưu thay đổi'}
          </Button>
        </DialogActions>
      </Dialog>

      <RolePermissionDialog
        open={permissionRole !== null}
        role={permissionRole}
        allPermissions={permissions}
        onClose={() => setPermissionRole(null)}
        onSaved={() => void load()}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Xóa vai trò"
        message={`Vai trò "${deleteTarget?.name}" sẽ bị xóa. Người dùng đang giữ vai trò này sẽ mất quyền tương ứng. Bạn có chắc chắn muốn tiếp tục?`}
        confirmLabel="Xóa"
        loading={deleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
